import c from 'classnames';
import { useMemo } from 'react';

import { Typography } from '@repo/ui';
import { Accordion, Markdown } from 'components';

import styles from './FAQ.module.css';

export const FAQ = ({
  faq,
  title,
  hideLastDivider = false,
  hideFirstDivider = false,
  firstQuestionStartsOpen = true,
  className,
  onQuestionExpand,
}: {
  faq: string;
  title?: string;
  hideLastDivider?: boolean;
  hideFirstDivider?: boolean;
  firstQuestionStartsOpen?: boolean;
  className?: string;
  // Fired with the question text when a question is expanded (not on collapse).
  onQuestionExpand?: (question: string) => void;
}) => {
  //faq: markdown string
  const faqData = useMemo(() => {
    if (!faq) return null;
    //this regular expression will match the headings that are meant to be the questions (content formatting guidelines)
    const regexp = /####.+(?=\n)/g;
    const matches = faq?.matchAll(regexp); //the questions still with markdown
    const questions = [];
    const answers = [];
    let previousEnd = null;
    //with the help of the "matches" we can parse the whole text into the questions and answers
    for (const match of matches) {
      questions.push(match[0].replace('####', '').trim());
      if (previousEnd) {
        answers.push(faq.substring(previousEnd + 1, match.index));
      }
      previousEnd = match.index
        ? match.index + match[0].length
        : match[0].length;
    }
    if (previousEnd) {
      answers.push(faq.substring(previousEnd + 1));
    }
    return { questions, answers };
  }, [faq]);

  if (!faqData || !faqData?.questions?.length || !faqData?.answers?.length)
    return <></>;

  return (
    <div className={className}>
      {!!title && (
        <Typography variant='heading-m' color='dark'>
          {title}
        </Typography>
      )}
      {faqData.answers.length === faqData.questions.length ? (
        faqData.questions.map((question, idx) => (
          <Accordion
            header={
              <Typography className={styles.question} variant='heading-s'>
                <Markdown>{question}</Markdown>
              </Typography>
            }
            initialState={idx === 0 && firstQuestionStartsOpen}
            onToggle={isOpen => {
              if (isOpen) onQuestionExpand?.(question);
            }}
            key={'question_' + idx}
            className={c(
              styles.accordionContainer,
              hideLastDivider &&
                idx === faqData.questions.length - 1 &&
                styles.withoutDivider,
              hideFirstDivider && idx === 0 && styles.withoutDividerTop,
            )}
          >
            <Typography as='div' variant='body-s'>
              <Markdown className={styles.markdown}>
                {faqData.answers[idx] as string}
              </Markdown>
            </Typography>
          </Accordion>
        ))
      ) : (
        <Markdown>{faq}</Markdown>
      )}
    </div>
  );
};
